// Drawer displaying concept classification (governance vocabulary) for a document.
import { useMemo, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from "../../../components/ui/sheet";
import type { ConceptClassifyResult } from "../api/contentRegistryApi";
import { formatPercent, plainText } from "../utils";
import {
  ConceptDrawerHeader,
  StatusBanner,
} from "./conceptIntelligenceParts";
import {
  ConceptGroupsSection,
  ExtractionMethodSection,
  ExtractionSummarySection,
  ProvenanceSection,
  RecurringThemesSection,
} from "./conceptIntelligenceSections";
import {
  averageConfidence,
  buildBySourceEntries,
  collectDefinedSections,
  computeConceptCounts,
  confidenceTone,
  flattenFindings,
  groupFindingsByGroup,
  maxOccurrences,
  partitionFindings,
  sortRecurringFindings,
  statusPill,
  type ConceptSourceFilter,
  type ConceptViewMode,
  CONCEPT_DRAWER_COPY,
  CONCEPT_DRAWER_OVERLAY_CLS,
  CONCEPT_DRAWER_SHEET_CLS,
  CONCEPT_VIEW_MODE,
} from "./utilities";
import { LoadingSpinner } from "../../SSP/components/LoadingSpinner";

export interface ConceptIntelligenceDrawerProps {
  open: boolean;
  onClose: () => void;
  result: ConceptClassifyResult | null;
  documentTitle?: string;
  documentType?: string;
  frameworkLabel?: string;
}

export function ConceptIntelligenceDrawer({
  open,
  onClose,
  result,
  documentTitle,
  documentType,
  frameworkLabel,
}: ConceptIntelligenceDrawerProps) {
  // ── Local UI state (grouped at top) ─────────────────────────────
  const [searchQuery, setSearchQuery] = useState("");
  const [sourceFilter, setSourceFilter] = useState<ConceptSourceFilter>("all");
  const [viewMode, setViewMode] = useState<ConceptViewMode>(
    CONCEPT_VIEW_MODE.ACCORDION,
  );
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    () => new Set(),
  );
  const [tablePage, setTablePage] = useState(0);

  // ── Handlers ────────────────────────────────────────────────────
  const resetDrawerState = () => {
    setSearchQuery("");
    setSourceFilter("all");
    setViewMode(CONCEPT_VIEW_MODE.ACCORDION);
    setCollapsedGroups(new Set());
    setTablePage(0);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) return;
    resetDrawerState();
    onClose();
  };

  const handleToggleGroup = (groupName: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) next.delete(groupName);
      else next.add(groupName);
      return next;
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setTablePage(0);
  };

  const handleSourceFilterChange = (value: ConceptSourceFilter) => {
    setSourceFilter(value);
    setTablePage(0);
  };

  // ── Derived data ────────────────────────────────────────────────
  const derived = useMemo(() => {
    const flat = flattenFindings(result);
    const { defined, recurring } = partitionFindings(flat);
    return {
      findings: flat,
      definedFindings: defined,
      recurringFindings: recurring,
      definedSections: collectDefinedSections(defined),
      sortedRecurring: sortRecurringFindings(recurring),
      recurringMax: maxOccurrences(recurring),
      avgDefinedConf: averageConfidence(defined),
      bySourceEntries: buildBySourceEntries(result, flat),
      conceptGroups: groupFindingsByGroup(flat),
    };
  }, [result]);

  const counts = useMemo(
    () =>
      computeConceptCounts({
        metrics: result?.metrics,
        findings: derived.findings,
        definedFindings: derived.definedFindings,
        recurringFindings: derived.recurringFindings,
      }),
    [result, derived.findings, derived.definedFindings, derived.recurringFindings],
  );

  const avgDefinedLabel = formatPercent(derived.avgDefinedConf);
  const avgDefinedTone = confidenceTone(derived.avgDefinedConf);

  const status = plainText(result?.status);
  const pill = statusPill(result?.status);
  const backendNote = result?.summary?.trim();
  const groupingPass = result?.metrics?.grouping_pass;
  const groupingMethod = result?.metrics?.grouping_method;
  const llmPass = result?.metrics?.llm_pass;
  const treeClauseText = result?.metrics?.tree_clause_text;

  const runMeta = {
    provider: plainText((result as { provider?: string })?.provider),
    modelId: plainText((result as { modelId?: string })?.modelId),
    elapsedMs: (result as { elapsedMs?: number })?.elapsedMs,
    layerVersion: (result as { layerVersion?: number })?.layerVersion,
    createdAt: (result as { createdAt?: string })?.createdAt,
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        overlayClassName={CONCEPT_DRAWER_OVERLAY_CLS}
        className={CONCEPT_DRAWER_SHEET_CLS}
      >
        <SheetTitle className="sr-only">{CONCEPT_DRAWER_COPY.title}</SheetTitle>
        <SheetDescription className="sr-only">
          {CONCEPT_DRAWER_COPY.description}
        </SheetDescription>

        {!result ? (
          <LoadingSpinner
            text={CONCEPT_DRAWER_COPY.loading}
            className="flex-1 justify-center"
          />
        ) : (
          <>
            <ConceptDrawerHeader
              documentMeta={{
                title: documentTitle,
                type: documentType,
                frameworkLabel,
              }}
              statusMeta={{ backendNote, pill }}
              confidenceMeta={{
                avgLabel: avgDefinedLabel,
                avgTextCls: avgDefinedTone.text,
                definedCount: counts.definedCount,
              }}
              runMeta={runMeta}
            />

            <div className="flex-1 overflow-y-auto p-4 sm:p-6 flex flex-col gap-4">
              <StatusBanner status={status} />

              <ExtractionSummarySection
                total={counts.total}
                definedSections={derived.definedSections}
                definedCount={counts.definedCount}
                definedCoveragePct={counts.definedCoveragePct}
                recurringCount={counts.recurringCount}
                topRecurringName={plainText(derived.sortedRecurring[0]?.name ?? "")}
                groupsCount={counts.groupsCount}
                groupingPass={groupingPass}
                ungrouped={counts.ungrouped}
                truncated={counts.truncated}
                findingsReturned={counts.findingsReturned}
              />

              <ConceptGroupsSection
                groups={derived.conceptGroups}
                searchQuery={searchQuery}
                sourceFilter={sourceFilter}
                viewMode={viewMode}
                collapsedGroups={collapsedGroups}
                tablePage={tablePage}
                onSearchChange={handleSearchChange}
                onSourceFilterChange={handleSourceFilterChange}
                onViewModeChange={setViewMode}
                onToggleGroup={handleToggleGroup}
                onTablePageChange={setTablePage}
              />

              <RecurringThemesSection
                sortedRecurring={derived.sortedRecurring}
                recurringMax={derived.recurringMax}
                groupingPass={groupingPass}
              />

              <ProvenanceSection entries={derived.bySourceEntries} />

              <ExtractionMethodSection
                llmPass={llmPass}
                groupingPass={groupingPass}
                groupingMethod={groupingMethod}
                treeClauseText={treeClauseText}
              />
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
